import {Box, HStack, Heading, Image, Text, VStack} from '@chakra-ui/react';
import {WhiteButton} from '@/components/Button/WhiteButton';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {useTranslation} from 'react-i18next';
import {Location, useNavigate} from 'react-router-dom';
import {useHalState} from '@synako/haligator';
import {useClient} from '@/haligator-factory';
import {isItemCatalogueMarketplace, isMarketplaceHit, routes} from 'ff-common';
import {HitItemCatalogue} from 'api-types/ffconnect';
import {DisplayLabels} from '@/domains/panier/components/DisplayLabels';
import {ArticleCodesDisplay} from '@/domains/panier/components/ArticleCodesDisplay';
import {TagCadencier} from '@/domains/cadencier/components/TagCadencier';
import {
  FicheProduit,
  FicheProduitPdfRow,
} from '@/domains/article/FicheArticle/FicheProduit';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {encodeURI} from '@/hooks/useFromFilters';
import {ModaleLayout} from './ModaleLayout';

type ApercuProduitModaleProps = {
  location: Location;
  imageUrl?: string;
} & (
  | {hit: HitItemCatalogue; code_article?: never}
  | {code_article: string; hit?: never}
);

/**
 * Modale d'aperçu produit — affiche image, libellé, marque, code, description et fiche PDF
 * sans quitter la page en cours.
 *
 * Deux modes d'entrée :
 * - `hit` : depuis le catalogue (résultat Orama, supporte FF + marketplace)
 * - `code_article` : depuis le panier/cadencier (FF uniquement)
 */
export const ApercuProduitModale = NiceModal.create<ApercuProduitModaleProps>(
  ({hit, code_article: codeProp, location, imageUrl}) => {
    const modal = useModal();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {buildClientRoute} = useClientRoute();
    const clientNav = useClient();

    const isMarketplace = hit ? isMarketplaceHit(hit) : false;
    const code = hit ? hit.code : codeProp;
    const marketplaceId = hit?.marketplace_id ?? 'FF';

    // Point d'entrée unique : item_catalogue_2, puis on suit les liens HAL
    const itemNav = clientNav.follow('go:item_catalogue_2', {
      marketplace_id: marketplaceId,
      code,
    });
    const {state: itemState} = useHalState(itemNav);
    // On vérifie la présence des liens HAL via hasOneLink pour éviter les erreurs console
    const hasArticle = itemState?.hasOneLink('article');
    const {state: articleState} = useHalState(
      hasArticle ? itemState?.follow('article') : undefined,
    );
    const {data: imagePrincipale} = useHalState(
      articleState?.follow('medias').follow('image_principale'),
    );
    const hasFicheTechnique = itemState?.hasOneLink(
      'fiche_technique_marketplace',
    );
    const {data: ficheTechniqueMarketplace} = useHalState(
      hasFicheTechnique
        ? itemState?.follow('fiche_technique_marketplace')
        : undefined,
    );

    // Affichage : hit prioritaire (instantané), sinon depuis l'article HAL
    const libelle =
      hit?.libelle_commercial ??
      articleState?.data.tolede.libelle_commercial ??
      '';
    const marque = hit?.marque ?? articleState?.data.tolede.marque?.[0];
    // Image : on prend la première URL disponible
    const finalImageUrl =
      imageUrl ??
      (hit?.vignette_url ? imageOptimisee(hit.vignette_url, 400) : undefined) ??
      (imagePrincipale?.url
        ? imageOptimisee(imagePrincipale.url, 400)
        : undefined);

    // Description : en marketplace, on utilise `avantages_produit`
    // En FF, on utilise `le_plus_produit`.
    const description = isMarketplace
      ? itemState && isItemCatalogueMarketplace(itemState.data)
        ? itemState.data.avantages_produit?.join('. ')
        : undefined
      : articleState?.data.tolede.le_plus_produit;

    // Redirection vers la fiche produit.
    const onVoirDetail = () => {
      modal.hide();
      const from = location.pathname.includes('/recherche')
        ? 'recherche'
        : undefined;
      const fromOpts = from
        ? {from, query: encodeURI(location.search)}
        : undefined;
      const route =
        isMarketplace && hit
          ? buildClientRoute(
              routes.client.produit_marketplace,
              {marketplace_id: hit.marketplace_id, ref_tiers: hit.ref_tiers},
              fromOpts,
            )
          : buildClientRoute(
              routes.client.produit,
              {code_article: code},
              fromOpts,
            );
      navigate(route);
    };

    return (
      <ModaleLayout
        visible={modal.visible}
        hide={modal.hide}
        titre={t('fiche_produit.apercu_produit')}
        fontSize='lg'
        size_desktop='xl'
        contentStyle={{
          borderRadius: {base: '0', md: 'xl'},
          maxH: '90vh',
          overflow: 'auto',
        }}
        closeButtonStyle={{bg: 'gray.lighter', borderRadius: 'full'}}
      >
        <VStack
          spacing='4'
          alignItems='stretch'
        >
          {/* Image produit */}
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minH='200px'
          >
            {finalImageUrl && (
              <Image
                src={finalImageUrl}
                alt={libelle}
                objectFit='contain'
                maxH='200px'
              />
            )}
          </Box>

          {/* Labels (FF uniquement) */}
          {!isMarketplace && articleState && (
            <HStack>
              <DisplayLabels articleState={articleState} />
            </HStack>
          )}

          {/* Code article */}
          <HStack>
            <ArticleCodesDisplay
              code_article={code}
              marketplace_id={marketplaceId}
              toledeData={
                itemState && !isItemCatalogueMarketplace(itemState.data)
                  ? itemState.data.tolede
                  : undefined
              }
            />
            {!isMarketplace && <TagCadencier code_article={code} />}
          </HStack>

          {/* Libellé + marque */}
          <Heading
            as='h4'
            size='md'
          >
            {libelle}
          </Heading>
          {marque && <Text color='gray.dark'>{marque}</Text>}

          {/* Description */}
          {description && (
            <Text
              fontSize='sm'
              color='gray.dark'
            >
              {description}
            </Text>
          )}

          {/* Fiche technique PDF */}
          <Box
            borderRadius='md'
            border='3px solid'
            borderColor='blue.sky_light'
            boxShadow='0 1px 4px rgba(0, 0, 0, 0.08)'
          >
            {!isMarketplace && articleState && (
              <FicheProduit
                articleState={articleState}
                showDocumentIcon
                showDownloadLabel
                borderColor='transparent'
              />
            )}
            {isMarketplace && ficheTechniqueMarketplace?.url && (
              <FicheProduitPdfRow
                url={ficheTechniqueMarketplace.url}
                label={t(
                  'fiche_produit.fiche_produit_pdf.telecharger_fiche_produit',
                )}
              />
            )}
          </Box>

          {/* Bouton "Voir le détail produit" */}
          <WhiteButton
            texte={t('fiche_produit.voir_detail_produit')}
            borderRadius='8px'
            w='fit-content'
            alignSelf='center'
            onClick={onVoirDetail}
          />
        </VStack>
      </ModaleLayout>
    );
  },
);
